import * as React from 'react';
import { cloneElement, Fragment } from 'react';
import {
    useListContext,
    TopToolbar,
    CreateButton,
    ExportButton,
    ShowButton,
    Button,
    sanitizeListRestProps,
    EditButton,
    useUpdateMany,
    useRefresh,
    useNotify,
    useUnselectAll,
    BulkDeleteWithUndoButton,
} from 'react-admin';
import { VisibilityOff, PictureAsPdf, Archive } from '@material-ui/icons';
import { FOOTSKILL_TOOLS_URL } from '../BASE_CONSTS';

const joinUrl = (base, path) => `${String(base || '').replace(/\/+$/, '')}/${String(path || '').replace(/^\/+/, '')}`;

const downloadBlob = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
};

const parseError = async (response) => {
    try {
        const payload = await response.json();
        return payload.detail || payload.message || `HTTP ${response.status}`;
    } catch (_error) {
        return `HTTP ${response.status}`;
    }
};

const requestBulkCertificate = async (selectedIds, format) => {
    const url = joinUrl(FOOTSKILL_TOOLS_URL, `admin/certificates/batch.${format}`);
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ footballer_ids: selectedIds }),
    });
    if (!response.ok) throw new Error(await parseError(response));
    const blob = await response.blob();
    const failed = Number(response.headers.get('X-FootSkill-Failed-Players') || 0);
    downloadBlob(blob, format === 'zip' ? 'footskill-certificates.zip' : 'footskill-certificates.pdf');
    return failed;
};

const ResetViewsButton = ({ selectedIds }) => {
    const refresh = useRefresh();
    const notify = useNotify();
    const unselectAll = useUnselectAll();
    const [updateMany, { loading }] = useUpdateMany(
        'posts',
        selectedIds,
        { views: 0 },
        {
            onSuccess: () => {
                refresh();
                notify('Posts updated');
                unselectAll('posts');
            },
            onFailure: error => notify('Error: posts not updated', 'warning'),
        }
    );

    return (
        <Button label="resetViews" disabled={loading} onClick={updateMany}>
            <VisibilityOff />
        </Button>
    );
};

export const PostBulkActionButtons = props => {
    const { basePath, data } = props;
    return (
        <Fragment>
            <ResetViewsButton {...props} />
            <ShowButton basePath={basePath} record={data} />
            <EditButton basePath={basePath} record={data} />
            <BulkDeleteWithUndoButton {...props} />
        </Fragment>
    );
};

const CertificateBulkButton = ({ selectedIds, format, label, icon }) => {
    const notify = useNotify();
    const unselectAll = useUnselectAll();
    const [loading, setLoading] = React.useState(false);

    const handleClick = async () => {
        if (!selectedIds || !selectedIds.length) return;
        setLoading(true);
        try {
            const failed = await requestBulkCertificate(selectedIds, format);
            if (failed) {
                notify(`Грамоты скачаны. Не удалось сформировать: ${failed}`, 'warning');
            } else {
                notify('Грамоты сформированы', 'info');
            }
            unselectAll('footballers');
        } catch (error) {
            notify(`Ошибка генерации грамот: ${error.message}`, 'warning');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button label={loading ? 'Формирование…' : label} disabled={loading} onClick={handleClick}>
            {icon}
        </Button>
    );
};

export const FootballerBulkActionButtons = props => (
    <Fragment>
        <CertificateBulkButton
            {...props}
            format="zip"
            label="ZIP грамот"
            icon={<Archive />}
        />
        <CertificateBulkButton
            {...props}
            format="pdf"
            label="Общий PDF"
            icon={<PictureAsPdf />}
        />
        <BulkDeleteWithUndoButton {...props} />
    </Fragment>
);

export const CertificateFootballer = ({ record }) => {
    if (!record || !record.id) return null;
    const href = joinUrl(FOOTSKILL_TOOLS_URL, `players/${record.id}/certificate.pdf`);
    return (
        <Button
            label="Грамота"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="fs-certificate-row-button"
        >
            <PictureAsPdf />
        </Button>
    );
};

export const ListActions = (props) => {
    const { className, exporter, filters, maxResults, ...rest } = props;
    const {
        currentSort,
        resource,
        displayedFilters,
        filterValues,
        hasCreate,
        basePath,
        showFilter,
        total,
    } = useListContext();
    return (
        <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
            {filters && cloneElement(filters, {
                resource,
                showFilter,
                displayedFilters,
                filterValues,
                context: 'button',
            })}
            {hasCreate ? <CreateButton basePath={basePath} /> : null}
            <ExportButton
                disabled={total === 0}
                resource={resource}
                sort={currentSort}
                filterValues={filterValues}
                maxResults={maxResults}
            />
        </TopToolbar>
    );
};

export const Edit = ({ record }) => (
    <EditButton basePath="/players" label="Редактировать" record={record} />
);

export const EditFootballers = ({ record }) => (
    <EditButton basePath="/footballers" label="Редактировать" record={record} />
);
export const EditPosts = ({ record }) => (
    <EditButton basePath="/Posts" label="Редактировать" record={record} />
);

export const EditShedule = ({ record }) => (
    <EditButton basePath="/scheduleas" label="Редактировать" record={record} />
);

export const Show = ({ record }) => (
    <ShowButton basePath="/players" label="Просмотр" record={record} />
);

export const ShowFootballers = ({ record }) => (
    <ShowButton basePath="/footballers" label="График" record={record} />
);

export const ShowPost = ({ record }) => (
    <ShowButton basePath="/Posts" label="Просмотр" record={record} />
);

export const ShowShedule = ({ record }) => (
    <ShowButton basePath="/scheduleas" label="Просмотр" record={record} />
);

export default {
    ListActions,
    PostBulkActionButtons,
    FootballerBulkActionButtons,
    CertificateFootballer,
    Edit,
    Show,
    ShowPost,
    EditPosts,
};
